import * as Sentry from "@sentry/react";
import type {
  OfflineChannel,
  OfflineChannelMessage,
  OfflineMessage,
  QueuedMessage,
} from "./types";

const DB_NAME = "streamChatOfflineDB";
const DB_VERSION = 3;

const MESSAGES_STORE = "messages";
const PENDING_TASKS_STORE = "pendingTasks";
const CHANNELS_STORE = "channels";
const CHANNEL_MESSAGES_STORE = "channelMessages";

/**
 * Utility to convert an IndexedDB request into a Promise based API.
 * This removes the need to manually attach onsuccess/onerror handlers
 * every time we need to retrieve the result of an `IDBRequest`.
 */
const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

class StreamChatOfflineDB {
  private db: IDBDatabase | null = null;

  private getDbInstance(): IDBDatabase {
    if (!this.db) {
      throw new Error("Database not initialized");
    }
    return this.db;
  }

  async init(): Promise<void> {
    // If database is already initialized, do nothing
    if (this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: { dbName: DB_NAME, dbVersion: DB_VERSION },
          tags: { action: "init-db", component: "stream-chat-offline-db" },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
          const messagesStore = db.createObjectStore(MESSAGES_STORE, {
            keyPath: "id",
          });
          messagesStore.createIndex("cid", "cid", { unique: false });
          messagesStore.createIndex("userId", "userId", { unique: false });
          messagesStore.createIndex("status", "status", { unique: false });
        }

        if (!db.objectStoreNames.contains(PENDING_TASKS_STORE)) {
          const tasksStore = db.createObjectStore(PENDING_TASKS_STORE, {
            keyPath: "id",
          });
          tasksStore.createIndex("cid", "cid", { unique: false });
          tasksStore.createIndex("type", "type", { unique: false });
        }

        if (!db.objectStoreNames.contains(CHANNELS_STORE)) {
          const channelsStore = db.createObjectStore(CHANNELS_STORE, {
            keyPath: "cid",
          });
          channelsStore.createIndex("type", "type", { unique: false });
          channelsStore.createIndex("last_message_at", "last_message_at", {
            unique: false,
          });
        }

        if (!db.objectStoreNames.contains(CHANNEL_MESSAGES_STORE)) {
          const channelMessagesStore = db.createObjectStore(
            CHANNEL_MESSAGES_STORE,
            {
              keyPath: "id",
            }
          );
          channelMessagesStore.createIndex("cid", "cid", { unique: false });
          channelMessagesStore.createIndex("created_at", "created_at", {
            unique: false,
          });
          channelMessagesStore.createIndex("user_id", "user.id", {
            unique: false,
          });
        }
      };
    });
  }

  async upsertMessage(message: OfflineMessage): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(MESSAGES_STORE);
      const request = store.put(message);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: { action: "upsert-message", messageId: message.id },
          tags: {
            action: "upsert-message",
            component: "stream-chat-offline-db",
          },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  async upsertMessages(messages: OfflineMessage[]): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(MESSAGES_STORE);

      let completed = 0;
      let hasError = false;

      messages.forEach((message) => {
        const request = store.put(message);

        request.onerror = () => {
          if (!hasError) {
            hasError = true;
            Sentry.captureException(request.error, {
              extra: {
                action: "upsert-messages",
                messageId: message.id,
                totalMessages: messages.length,
              },
              tags: {
                action: "upsert-messages",
                component: "stream-chat-offline-db",
              },
            });
            reject(request.error);
          }
        };

        request.onsuccess = () => {
          completed++;
          if (completed === messages.length && !hasError) {
            resolve();
          }
        };
      });
    });
  }

  async getMessagesByChannel(cid: string): Promise<OfflineMessage[]> {
    const db = this.getDbInstance();
    const store = db
      .transaction([MESSAGES_STORE], "readonly")
      .objectStore(MESSAGES_STORE);
    const index = store.index("cid");
    const messagesRequest = index.getAll(cid) as IDBRequest<OfflineMessage[]>;

    const messages = await requestToPromise(messagesRequest);
    return messages;
  }

  async getPendingMessages(): Promise<OfflineMessage[]> {
    const db = this.getDbInstance();
    const store = db
      .transaction([MESSAGES_STORE], "readonly")
      .objectStore(MESSAGES_STORE);
    const index = store.index("status");
    const messagesRequest = index.getAll("sending") as IDBRequest<
      OfflineMessage[]
    >;

    const messages = await requestToPromise(messagesRequest);
    return messages;
  }

  async updateMessageStatus(
    messageId: string,
    status: "failed" | "received" | "sending" | "sent"
  ): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(MESSAGES_STORE);

      const getRequest = store.get(messageId);

      getRequest.onerror = () => {
        Sentry.captureException(getRequest.error, {
          extra: { action: "get-message-for-status-update", messageId },
          tags: {
            action: "update-message-status",
            component: "stream-chat-offline-db",
          },
        });
        reject(getRequest.error);
      };

      getRequest.onsuccess = () => {
        const message = getRequest.result as OfflineMessage;
        if (!message) {
          reject(new Error(`Message ${messageId} not found`));
          return;
        }

        const updatedMessage = {
          ...message,
          status,
          updated_at: new Date().toISOString(),
        };

        const putRequest = store.put(updatedMessage);

        putRequest.onerror = () => {
          Sentry.captureException(putRequest.error, {
            extra: { action: "update-message-status", messageId, status },
            tags: {
              action: "update-message-status",
              component: "stream-chat-offline-db",
            },
          });
          reject(putRequest.error);
        };

        putRequest.onsuccess = () => {
          resolve();
        };
      };
    });
  }

  async deleteMessage(messageId: string): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(MESSAGES_STORE);
      const request = store.delete(messageId);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: { action: "delete-message", messageId },
          tags: {
            action: "delete-message",
            component: "stream-chat-offline-db",
          },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  async addQueuedMessage(message: QueuedMessage): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PENDING_TASKS_STORE], "readwrite");
      const store = transaction.objectStore(PENDING_TASKS_STORE);
      const request = store.put(message);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: {
            action: "add-queued-message",
            messageId: message.id,
            messageType: message.type,
          },
          tags: {
            action: "add-queued-message",
            component: "stream-chat-offline-db",
          },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  async getQueuedMessages(): Promise<QueuedMessage[]> {
    const db = this.getDbInstance();
    const store = db
      .transaction([PENDING_TASKS_STORE], "readonly")
      .objectStore(PENDING_TASKS_STORE);
    const messagesRequest = store.getAll() as IDBRequest<QueuedMessage[]>;

    const messages = await requestToPromise(messagesRequest);
    return messages;
  }

  async removeQueuedMessage(messageId: string): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PENDING_TASKS_STORE], "readwrite");
      const store = transaction.objectStore(PENDING_TASKS_STORE);
      const request = store.delete(messageId);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: { action: "remove-queued-message", messageId },
          tags: {
            action: "remove-queued-message",
            component: "stream-chat-offline-db",
          },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  async clear(): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [
          MESSAGES_STORE,
          PENDING_TASKS_STORE,
          CHANNELS_STORE,
          CHANNEL_MESSAGES_STORE,
        ],
        "readwrite"
      );

      const messagesStore = transaction.objectStore(MESSAGES_STORE);
      const tasksStore = transaction.objectStore(PENDING_TASKS_STORE);
      const channelsStore = transaction.objectStore(CHANNELS_STORE);
      const channelMessagesStore = transaction.objectStore(
        CHANNEL_MESSAGES_STORE
      );

      let completed = 0;
      const totalStores = 4;

      const checkComplete = () => {
        completed++;
        if (completed === totalStores) {
          resolve();
        }
      };

      messagesStore.clear().onsuccess = checkComplete;
      tasksStore.clear().onsuccess = checkComplete;
      channelsStore.clear().onsuccess = checkComplete;
      channelMessagesStore.clear().onsuccess = checkComplete;

      transaction.onerror = () => {
        Sentry.captureException(transaction.error, {
          extra: { action: "clear-db" },
          tags: { action: "clear-db", component: "stream-chat-offline-db" },
        });
        reject(transaction.error);
      };
    });
  }

  async upsertChannel(channel: OfflineChannel): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([CHANNELS_STORE], "readwrite");
      const store = transaction.objectStore(CHANNELS_STORE);
      const request = store.put(channel);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: { action: "upsert-channel", channelId: channel.cid },
          tags: {
            action: "upsert-channel",
            component: "stream-chat-offline-db",
          },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  async getChannel(cid: string): Promise<OfflineChannel | null> {
    const db = this.getDbInstance();
    const store = db
      .transaction([CHANNELS_STORE], "readonly")
      .objectStore(CHANNELS_STORE);
    const channelRequest = store.get(cid) as IDBRequest<
      OfflineChannel | undefined
    >;

    const channel = await requestToPromise(channelRequest);
    return channel ?? null;
  }

  async getAllChannels(): Promise<OfflineChannel[]> {
    const db = this.getDbInstance();
    const store = db
      .transaction([CHANNELS_STORE], "readonly")
      .objectStore(CHANNELS_STORE);
    const channelsRequest = store.getAll() as IDBRequest<OfflineChannel[]>;
    return requestToPromise(channelsRequest);
  }

  async upsertChannelMessage(message: OfflineChannelMessage): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([CHANNEL_MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(CHANNEL_MESSAGES_STORE);
      const request = store.put(message);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: { action: "upsert-channel-message", messageId: message.id },
          tags: {
            action: "upsert-channel-message",
            component: "stream-chat-offline-db",
          },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  async upsertChannelMessages(
    messages: OfflineChannelMessage[]
  ): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([CHANNEL_MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(CHANNEL_MESSAGES_STORE);

      let completed = 0;
      const totalMessages = messages.length;

      if (totalMessages === 0) {
        resolve();
        return;
      }

      const checkComplete = () => {
        completed++;
        if (completed === totalMessages) {
          resolve();
        }
      };

      messages.forEach((message) => {
        const request = store.put(message);
        request.onsuccess = checkComplete;
        request.onerror = () => {
          Sentry.captureException(request.error, {
            extra: {
              action: "upsert-channel-messages",
              messageId: message.id,
              totalMessages,
            },
            tags: {
              action: "upsert-channel-messages",
              component: "stream-chat-offline-db",
            },
          });
          reject(request.error);
        };
      });

      transaction.onerror = () => {
        Sentry.captureException(transaction.error, {
          extra: { action: "upsert-channel-messages-transaction" },
          tags: {
            action: "upsert-channel-messages",
            component: "stream-chat-offline-db",
          },
        });
        reject(transaction.error);
      };
    });
  }

  async getChannelMessages(cid: string): Promise<OfflineChannelMessage[]> {
    const db = this.getDbInstance();
    const store = db
      .transaction([CHANNEL_MESSAGES_STORE], "readonly")
      .objectStore(CHANNEL_MESSAGES_STORE);
    const index = store.index("cid");
    const messagesRequest = index.getAll(cid) as IDBRequest<
      OfflineChannelMessage[]
    >;

    const messages = await requestToPromise(messagesRequest);
    messages.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    return messages;
  }

  async deleteChannel(cid: string): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [CHANNELS_STORE, CHANNEL_MESSAGES_STORE],
        "readwrite"
      );

      const channelsStore = transaction.objectStore(CHANNELS_STORE);
      const messagesStore = transaction.objectStore(CHANNEL_MESSAGES_STORE);
      const messagesIndex = messagesStore.index("cid");

      const channelRequest = channelsStore.delete(cid);

      const messagesRequest = messagesIndex.openCursor(IDBKeyRange.only(cid));

      let completed = 0;
      const totalOperations = 2;

      const checkComplete = () => {
        completed++;
        if (completed === totalOperations) {
          resolve();
        }
      };

      channelRequest.onsuccess = checkComplete;
      channelRequest.onerror = () => {
        Sentry.captureException(channelRequest.error, {
          extra: { action: "delete-channel", channelId: cid },
          tags: {
            action: "delete-channel",
            component: "stream-chat-offline-db",
          },
        });
        reject(channelRequest.error);
      };

      messagesRequest.onsuccess = (event) => {
        const cursor = event.target as IDBCursorWithValue | null;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          checkComplete();
        }
      };

      messagesRequest.onerror = () => {
        Sentry.captureException(messagesRequest.error, {
          extra: { action: "delete-channel-messages", channelId: cid },
          tags: {
            action: "delete-channel",
            component: "stream-chat-offline-db",
          },
        });
        reject(messagesRequest.error);
      };

      transaction.onerror = () => {
        Sentry.captureException(transaction.error, {
          extra: { action: "delete-channel-transaction", channelId: cid },
          tags: {
            action: "delete-channel",
            component: "stream-chat-offline-db",
          },
        });
        reject(transaction.error);
      };
    });
  }

  /**
   * Delete a message stored in CHANNEL_MESSAGES_STORE by its ID.
   * Used when the global queue successfully forwards a pending message and
   * we no longer want it to reappear in offline mode.
   */
  async deleteChannelMessage(messageId: string): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([CHANNEL_MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(CHANNEL_MESSAGES_STORE);
      const request = store.delete(messageId);

      request.onerror = () => {
        Sentry.captureException(request.error, {
          extra: { action: "delete-channel-message", messageId },
          tags: {
            action: "delete-channel-message",
            component: "stream-chat-offline-db",
          },
        });
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Update status and pending flag for a message in CHANNEL_MESSAGES_STORE.
   */
  async updateChannelMessageStatus(
    messageId: string,
    status: "failed" | "received" | "sending" | "sent",
    pending: boolean
  ): Promise<void> {
    const db = this.getDbInstance();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([CHANNEL_MESSAGES_STORE], "readwrite");
      const store = transaction.objectStore(CHANNEL_MESSAGES_STORE);
      const getRequest = store.get(messageId);

      getRequest.onerror = () => {
        Sentry.captureException(getRequest.error, {
          extra: { action: "get-channel-message-for-status-update", messageId },
          tags: {
            action: "update-channel-message-status",
            component: "stream-chat-offline-db",
          },
        });
        reject(getRequest.error);
      };

      getRequest.onsuccess = () => {
        const msg = getRequest.result as OfflineChannelMessage | undefined;
        if (!msg) {
          resolve();
          return;
        }

        const updated = {
          ...msg,
          pending_message: pending,
          status,
          updated_at: new Date().toISOString(),
        } as OfflineChannelMessage;

        const putReq = store.put(updated);
        putReq.onerror = () => {
          Sentry.captureException(putReq.error, {
            extra: {
              action: "update-channel-message-status",
              messageId,
              pending,
              status,
            },
            tags: {
              action: "update-channel-message-status",
              component: "stream-chat-offline-db",
            },
          });
          reject(putReq.error);
        };
        putReq.onsuccess = () => resolve();
      };
    });
  }
}

export {
  type OfflineChannel,
  type OfflineChannelMessage,
  type OfflineMessage,
  type QueuedMessage,
  StreamChatOfflineDB,
};
